<template>
  <div class="auth">
    <div class="auth__card reveal">
      <div class="auth__peel">🍌</div>
      <h1 class="auth__title">{{ isLogin ? 'Content de te revoir' : 'Rejoins la banane' }}</h1>
      <p class="auth__sub">
        {{ isLogin ? 'Connecte-toi pour voter et proposer tes perles.' : 'Un compte, et tu peux voter et proposer tes blagues.' }}
      </p>

      <form class="auth__form" @submit.prevent="submit">
        <label class="field">
          <span class="field__label">Email</span>
          <input
            v-model.trim="email"
            type="email"
            class="field__input"
            placeholder="toi@exemple.fr"
            autocomplete="email"
            required
          />
        </label>

        <label v-if="!isLogin" class="field">
          <span class="field__label">Pseudo <em>(optionnel)</em></span>
          <input
            v-model.trim="displayName"
            type="text"
            class="field__input"
            placeholder="Le Banané Masqué"
            maxlength="50"
          />
        </label>

        <label class="field">
          <span class="field__label">Mot de passe</span>
          <div class="field__pwd">
            <input
              v-model="password"
              :type="showPwd ? 'text' : 'password'"
              class="field__input"
              placeholder="••••••••"
              :autocomplete="isLogin ? 'current-password' : 'new-password'"
              required
            />
            <button type="button" class="field__eye" @click="showPwd = !showPwd" :aria-label="showPwd ? 'Masquer' : 'Afficher'">
              {{ showPwd ? '🙈' : '👁️' }}
            </button>
          </div>
        </label>

        <p v-if="error" class="auth__error">⚠️ {{ error }}</p>

        <BananaButton type="submit" size="lg" block :loading="loading">
          {{ isLogin ? 'Se connecter' : 'Créer mon compte' }}
        </BananaButton>
      </form>

      <!-- Google Sign-In. Only shown if VUE_APP_GOOGLE_CLIENT_ID is configured;
           in dev without the env var, this whole block stays hidden. -->
      <template v-if="googleClientId">
        <div class="auth__divider"><span>ou</span></div>
        <div ref="googleBtn" class="auth__google"></div>
        <p v-if="googleError" class="auth__error auth__error--google">⚠️ {{ googleError }}</p>
      </template>

      <button class="auth__toggle" @click="toggleMode">
        {{ isLogin ? "Pas encore de compte ? S'inscrire" : 'Déjà un compte ? Se connecter' }}
      </button>
    </div>
  </div>
</template>

<script>
import { LOGIN, REGISTER, GOOGLE_AUTH } from '@/graphql/auth'
import { setAuth } from '@/auth'
import BananaButton from '@/components/ui/BananaButton.vue'

const GIS_SRC = 'https://accounts.google.com/gsi/client'

export default {
  name: 'PageLogin',
  components: { BananaButton },
  data() {
    return {
      mode: 'login',
      email: '',
      password: '',
      displayName: '',
      showPwd: false,
      loading: false,
      error: '',
      googleClientId: process.env.VUE_APP_GOOGLE_CLIENT_ID || '',
      googleError: '',
    }
  },
  computed: {
    isLogin() { return this.mode === 'login' },
  },
  mounted() {
    if (this.googleClientId) this.loadGoogleScript()
  },
  methods: {
    toggleMode() {
      this.mode = this.isLogin ? 'register' : 'login'
      this.error = ''
    },
    loadGoogleScript() {
      // Reuse the GIS script if it's already in the page, else inject it.
      const existing = document.querySelector(`script[src="${GIS_SRC}"]`)
      if (window.google && window.google.accounts && window.google.accounts.id) {
        this.initGoogle()
        return
      }
      if (existing) {
        existing.addEventListener('load', () => this.initGoogle(), { once: true })
        return
      }
      const script = document.createElement('script')
      script.src = GIS_SRC
      script.async = true
      script.defer = true
      script.onload = () => this.initGoogle()
      script.onerror = () => { this.googleError = 'Impossible de charger Google Sign-In.' }
      document.head.appendChild(script)
    },
    initGoogle() {
      if (!window.google || !this.$refs.googleBtn) return
      window.google.accounts.id.initialize({
        client_id: this.googleClientId,
        callback: this.handleGoogleCredential,
      })
      window.google.accounts.id.renderButton(this.$refs.googleBtn, {
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        text: 'continue_with',
        width: 320,
        locale: 'fr',
      })
    },
    async handleGoogleCredential(response) {
      this.googleError = ''
      const idToken = response && response.credential
      if (!idToken) {
        this.googleError = 'Connexion Google annulée ou refusée.'
        return
      }
      try {
        const { data } = await this.$apollo.mutate({
          mutation: GOOGLE_AUTH,
          variables: { idToken },
        })
        const payload = data.googleAuth
        setAuth({ token: payload.token, refreshToken: payload.refreshToken, user: payload.user })
        this.$router.push(this.$route.query.redirect || '/')
      } catch (e) {
        this.googleError = e?.graphQLErrors?.[0]?.message || e?.message || 'La connexion Google a échoué.'
      }
    },
    async submit() {
      this.loading = true
      this.error = ''
      try {
        const { data } = await this.$apollo.mutate({
          mutation: this.isLogin ? LOGIN : REGISTER,
          variables: this.isLogin
            ? { email: this.email, password: this.password }
            : { email: this.email, password: this.password, displayName: this.displayName },
        })
        const payload = this.isLogin ? data.tokenAuth : data.register
        setAuth({ token: payload.token, refreshToken: payload.refreshToken, user: payload.user })
        this.$router.push(this.$route.query.redirect || '/')
      } catch (e) {
        this.error = e?.graphQLErrors?.[0]?.message || e?.message || 'Une erreur est survenue.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.auth { display: flex; justify-content: center; align-items: flex-start; padding: 60px 20px 80px; }
.auth__card {
  position: relative;
  width: 100%;
  max-width: 440px;
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-xl);
  box-shadow: var(--shadow-lg);
  padding: 46px 34px 30px;
  text-align: center;
}
.auth__peel {
  position: absolute; top: -26px; left: 50%; transform: translateX(-50%);
  font-size: 3rem; filter: drop-shadow(2px 3px 0 var(--border-c));
}
.auth__title { font-size: 1.9rem; margin-bottom: 6px; }
.auth__sub { color: var(--text-soft); margin: 0 0 26px; font-size: 0.98rem; }

.auth__form { display: flex; flex-direction: column; gap: 16px; text-align: left; }
.field { display: flex; flex-direction: column; gap: 6px; }
.field__label { font-weight: 700; font-size: 0.85rem; color: var(--ink-800); }
.field__label em { font-family: var(--font-accent); font-weight: 400; color: var(--text-soft); }
.field__input {
  width: 100%;
  font-family: var(--font-body);
  font-size: 1rem;
  padding: 12px 15px;
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-md);
  background: var(--banana-50);
  color: var(--ink-900);
  transition: box-shadow 0.15s, background 0.15s;
}
.field__input::placeholder { color: var(--ink-300); }
.field__input:focus {
  outline: none;
  background: #fff;
  box-shadow: 4px 4px 0 var(--banana-500);
}
.field__pwd { position: relative; }
.field__eye {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; font-size: 1.1rem; padding: 4px;
}

.auth__error {
  background: color-mix(in srgb, var(--bruise) 12%, white);
  border: 1.5px solid var(--bruise);
  color: var(--bruise);
  padding: 10px 14px;
  border-radius: var(--r-sm);
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
}
.auth__divider {
  display: flex; align-items: center; gap: 12px;
  margin: 22px 0 18px;
  color: var(--text-soft); font-size: 0.85rem; font-weight: 600;
}
.auth__divider::before, .auth__divider::after {
  content: ''; flex: 1; height: 2px; background: var(--ink-900); opacity: 0.15;
}
.auth__google { display: flex; justify-content: center; min-height: 44px; }
.auth__error--google { margin-top: 14px; text-align: left; }

.auth__toggle {
  margin-top: 22px;
  background: none; border: none; cursor: pointer;
  font-family: var(--font-body); font-weight: 600; font-size: 0.92rem;
  color: var(--banana-700);
  text-decoration: underline;
  text-underline-offset: 3px;
}
.auth__toggle:hover { color: var(--bruise); }

@media (max-width: 560px) {
  .auth { padding: 40px 16px 60px; }
  .auth__card { padding: 40px 22px 26px; }
  .auth__title { font-size: 1.6rem; }
}
</style>
